<template>
  <el-form
    class="profile-tab"
    :style="{ height: height, overflow: 'scroll', 'padding-bottom': '50px' }"
    :key="attributes.length"
  >
    <el-form-item :label="$t('CampaignForm_Preview_Profile_Title')">
      <el-select
        v-model="profileId"
        :loading="loading"
        filterable
        remote
        @change="onChange"
        :remote-method="fetchProfiles"
        clearable
        @clear="fetchProfiles"
      >
        <el-option-group
          v-if="seedProfiles.length"
          :label="$t('CampaignForm_Preview_Profile_Dropdown_SeedList')"
        >
          <el-option
            v-for="(item, index) in seedProfiles"
            :key="index"
            :label="
              item.email ||
                ($hasFeature('customerIdEnabled') ? item.customer_id : null) ||
                item.user_id ||
                item.id
            "
            :value="item.id"
          />
        </el-option-group>

        <el-option-group
          v-if="profiles.length"
          :label="$t('CampaignForm_Preview_Profile_Dropdown_AllProfiles')"
        >
          <el-option
            v-for="(item, index) in profiles"
            :key="index"
            :label="
              item.email ||
                ($hasFeature('customerIdEnabled') ? item.customer_id : null) ||
                item.user_id ||
                item.id
            "
            :value="item.id"
          />
        </el-option-group>
      </el-select>
    </el-form-item>
    <form-item
      class="flexbox"
      v-for="(val, attr) in attributes"
      :label="attr"
      :key="attr"
    >
      <el-tooltip
        :content="getUnmetTooltipMessage(attr)"
        placement="top"
        effect="light"
        :disabled="!hasUnmetAttributeConditions(attr)"
      >
        <el-input
          class="borderless"
          :class="getUnmetConditionsClass(attr)"
          v-model="attributes[attr]"
          placeholder="-"
          readonly
        />
      </el-tooltip>
    </form-item>
  </el-form>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { $hasFeature } from '@/core/utils/VueAcl'
import _ from 'lodash'
import { EventBus } from '@/core/EventBus'
import FormItem from '@/common/components/form/FormItem.vue'

export default {
  name: 'PreviewContentUserProfileTab',
  components: { FormItem },
  data() {
    return {
      height: '450px',
      attributes: [],
      profileId: null,
      data: [],
      profiles: [],
      seedProfiles: [],
      loading: false,
      unmetConditions: []
    }
  },
  mounted() {
    this.calculateHeight()
    this.fetchProfiles(null, true)
    EventBus.$on('set-profile-attributes', attributes => {
      this.attributes = attributes
    })
    EventBus.$on('set-unmet-attribute-conditions', unmetConditions => {
      this.unmetConditions = unmetConditions
    })
  },
  computed: {
    seedListEnabled() {
      return $hasFeature('featureSeedList') && $hasFeature('seedListEnabled')
    }
  },
  methods: {
    $hasFeature,
    fetchNonSeedListProfilesEndpoint(params = {}) {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: { in_seed_list: 0, ...params },
        handler: data => {
          this.profiles = data
        }
      })
    },
    fetchSeedListProfilesEndpoint(params = {}) {
      if (!this.seedListEnabled) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: { in_seed_list: 1, ...params },
        handler: data => {
          this.seedProfiles = data
        }
      })
    },
    onChange(profileId) {
      Object.keys(this.attributes).forEach(attr => {
        this.attributes[attr] = null
      })
      this.$emit('data-change', profileId)
    },
    fetchProfiles(query, firstLoad = false) {
      let params = {}

      if (query && query.length === 32 && !query.includes('@')) {
        params.id = query
      } else if (query) {
        params.email = query
      }

      this.loading = true

      let promises = []
      let fetchEndpoints = [
        this.fetchNonSeedListProfilesEndpoint(params),
        this.fetchSeedListProfilesEndpoint(params)
      ]
      fetchEndpoints.forEach(endpoint => {
        if (endpoint) {
          promises.push(
            RestApi(endpoint)
              .then(({ data }) => {
                endpoint.handler(data)
              })
              .catch(error => {
                this.loading = false
                this.$message({
                  message: error.message,
                  type: 'error'
                })
              })
          )
        }
      })

      Promise.all(promises)
        .then(() => {
          if (firstLoad && !this.profileId) {
            this.profileId =
              _.get(this.seedProfiles, '[0].id') ||
              _.get(this.profiles, '[0].id') ||
              null

            if (this.profileId) {
              this.onChange(this.profileId)
            }
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    calculateHeight() {
      // Calculate dynamic height for attributes section
      let height = 0
      let minHeight = 250
      if (!document.querySelector('.sidebar-ct')) {
        this.height = '450px'
      }

      let sidebarHeight = document.querySelector('.sidebar-ct').clientHeight
      let optionsHeight = 0
      document.querySelectorAll('.campaign-preview-option').forEach(el => {
        optionsHeight += el.clientHeight || 0
      })
      height = sidebarHeight - optionsHeight - 165

      this.height = Math.max(height, minHeight) + 'px'
    },
    isUnmetAttributeConditionWithFallback(attribute) {
      return !!this.unmetConditions['unmetConditionAttributes'].includes(
        attribute
      )
    },
    isUnmetAttributeConditionWithNoFallback(attribute) {
      const key = 'unmetConditionAttributesNoFallback'
      const unmetConditions = this.unmetConditions[key]

      return !!unmetConditions.includes(attribute)
    },
    hasUnmetAttributeConditions(attribute) {
      return (
        this.isUnmetAttributeConditionWithFallback(attribute) ||
        this.isUnmetAttributeConditionWithNoFallback(attribute)
      )
    },
    getUnmetConditionsClass(attribute) {
      return { 'unmet-conditions': this.hasUnmetAttributeConditions(attribute) }
    },
    getUnmetTooltipMessage(attribute) {
      if (this.isUnmetAttributeConditionWithFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_Fallback_Applied'
        )
      }
      if (this.isUnmetAttributeConditionWithNoFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_No_Fallback_Available'
        )
      }
    }
  },
  destroyed() {
    EventBus.$off('set-profile-attributes')
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
}

.flexbox:before,
.flexbox::after {
  display: inherit;
  content: none;
}

/deep/ .flexbox .el-form-item__label {
  font-size: 12px;
  font-weight: 600;
  padding-top: 4px;
}

/deep/ .borderless > .el-input__inner {
  border: 0;
  border-bottom: 1px solid #c2c7d3;
  border-radius: 0;
}

/deep/ .unmet-conditions > .el-input__inner {
  background-color: #fde8e9;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/content-data-tabs/PreviewContentUserProfileTab.vue